import { ifDefined } from 'lit/directives/if-defined.js'
import { classMap } from 'lit/directives/class-map.js'
import { property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'

import { ariaElements, delegateEvents, refCollection } from '#utils/directives'
import LfdsIcon from '#components/icon/icon/lfds-icon.ts'
import { LfuiElement } from '#utils/element'
import {
	WithInternals,
	WithFocus,
	internals,
	compose,
	anchor
} from '#utils/mixins'

import styles from './lfds-textarea.css.ts'

const DELEGATED_EVENTS = [
	'input',
	'change',
	'focus',
	'blur',
	'focusin',
	'focusout',
	'keydown',
	'keyup',
	'keypress',
	'paste',
	'cut',
	'copy',
	'select',
	'reset'
]

/**
 * @name LfdsTextarea
 *
 * @deprecated Moved to `@lansforsakringar/core-recipes/textarea` as `<lfds-r-textarea>`.
 * @tag lfds-textarea
 * @status deprecated
 * @slot label - Textarea label
 */
export default class LfdsTextarea extends compose(
	LfuiElement,
	WithInternals<HTMLTextAreaElement>,
	WithFocus
) {
	static styles = [LfuiElement.styles, styles]

	// React is kind of funny about events and and onChange isn't really a change
	// event, so by declaring it as an event here we will bypass React's native
	// event handling and have @lit/react handle it correctly.
	static events = ['change']

	@property() value: string | number | File = ''
	@property({ type: Number }) maxlength: number
	@property({ type: Number }) minlength: number
	@property() wrap: 'soft' | 'hard'
	@property() dirname: string
	@property() label!: string
	@property() min: string
	@property() max: string
	@property() name: string
	@property({ type: Number }) rows: number = 4
	@property({ attribute: 'help-text' }) helpText: string
	@property({ attribute: 'validation-message' }) validationMessage: string
	@property({ type: Boolean }) required: boolean
	@property({ type: Boolean }) disabled: boolean
	@property({ type: Boolean }) readonly: boolean = false
	@property({ type: Boolean }) autofocus: boolean = false

	/**
	 * Remember to set `validation-message` if the textarea is invalid,
	 * as it is both conveyed to the user and used for the textarea's setCustomValidity method.
	 */
	@property({ type: Boolean }) invalid: boolean

	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string
	@property({ attribute: 'sr-controls' }) srControls: string

	#labels = refCollection()
	#descriptions = refCollection()

	constructor() {
		super()
		LfdsIcon.preload('alert', 24)
	}

	connectedCallback() {
		super.connectedCallback()

		this[internals].form?.addEventListener('reset', () => {
			this.invalid = false
			this.value = ''
		})

		// Handle calling reportValidity on parent form
		this.addEventListener('invalid', () => {
			this.invalid = true
		})
	}

	focus() {
		this[anchor].value?.focus()
	}

	select() {
		this[anchor].value?.select()
	}

	setSelectionRange(start: number, end: number) {
		this[anchor].value?.setSelectionRange(start, end)
	}

	updated(changed) {
		if (changed.has('value')) {
			const data = new FormData()
			const value =
				typeof this.value === 'number' ? String(this.value) : this.value
			data.set(this.name, value)
			this[internals].setFormValue(data)
		}

		const input = this[anchor].value
		const { maxlength, minlength } = this
		if (this.validationMessage) {
			this[internals].setValidity(input.validity, this.validationMessage, input)
		} else if (changed.has('value') || maxlength || minlength) {
			// Since we're not using native min/maxlength which will prevent input
			// beyond the max length, we need to handle it manually.
			const value = String(this.value || '')
			let validity = input.validity
			let message = this.validationMessage || input.validationMessage
			if (value.length > maxlength) {
				validity = { tooLong: true } as ValidityState
				message = `För många tecken: ${(maxlength - value.length) * -1}`
			} else if (value.length < minlength) {
				validity = { tooShort: true } as ValidityState
				message = `För få tecken: ${minlength - value.length}`
			}
			this[internals].setValidity(validity, message, input)
		}
	}

	#oninput = (event: KeyboardEvent) => {
		this.value = (event.target as HTMLTextAreaElement).value
		this.invalid = false
	}

	#onclick(event: MouseEvent) {
		this[anchor].value.select()
	}

	render() {
		const value = String(this.value ?? '')
		const charsCount = value.length
		const charsLeft = this.maxlength - charsCount
		const ariaLabel = this.srLabel ?? undefined

		return html`
			<label
				class="label"
				for="textarea"
				${this.#labels.add()}
				@click=${this.#onclick}
			>
				<slot name="label">${this.label}</slot>
			</label>
			<div
				class=${classMap({
					box: true,
					invalid: this.invalid,
					['focus-visible']: this.focusVisible
				})}
			>
				<textarea
					id="textarea"
					class="textarea"
					placeholder=" "
					aria-invalid=${this.invalid ? 'true' : (nothing as any)}
					aria-label=${ifDefined(ariaLabel)}
					wrap=${ifDefined(this.wrap)}
					dirname=${ifDefined(this.dirname)}
					max=${ifDefined(this.max)}
					min=${ifDefined(this.min)}
					name=${ifDefined(this.name)}
					rows=${ifDefined(this.rows)}
					?autofocus=${this.autofocus}
					?disabled=${this.disabled}
					?required=${this.required}
					?readonly=${this.readonly}
					@input=${this.#oninput}
					${ref(this[anchor])}
					${this.#labels.bind('ariaLabelledByElements')}
					${this.#descriptions.bind('ariaDescribedByElements')}
					${delegateEvents(DELEGATED_EVENTS, {
						before: { input: this.#oninput }
					})}
					${ariaElements({
						describedBy: this.srDescribedby,
						labelledBy: this.srLabelledby,
						controls: this.srControls
					})}
				>
${value}</textarea>
			</div>
			<div
				class=${classMap({
					footer: true,
					wrap: this.helpText && this.validationMessage
				})}
			>
				${
					this.helpText
						? html`
								<span class="help" ${this.#descriptions.add()}>
									${this.helpText}
								</span>
							`
						: nothing
				}
				${
					this.validationMessage
						? html`
								<div aria-atomic="true" role="alert" class="validation">
									<span class="icon">
										<lfds-icon
											icon-id="alert"
											size="24"
											color="currentcolor"
										></lfds-icon>
									</span>
									<span ${this.#labels.add()}>${this.validationMessage}</span>
								</div>
							`
						: nothing
				}
				${
					this.maxlength
						? html`
								<span
									class=${classMap({
										help: true,
										counter: true,
										warning:
											charsCount > this.maxlength || charsCount < this.minlength
									})}
								>
									<span aria-hidden="true">
										${charsCount}/${this.maxlength}
									</span>
									<span class="hidden" ${this.#labels.add()}>
										<span aria-live="polite">
											${
												charsLeft < 6 && charsLeft > 0
													? `Antal tecken kvar: ${charsLeft}`
													: nothing
											}
										</span>
										<span aria-live="assertive">
											${charsLeft === 0 ? 'Max antal tecken' : nothing}
										</span>
										<span aria-live="polite">
											${
												charsLeft < 0
													? `För många tecken: ${charsLeft * -1}`
													: nothing
											}
										</span>
									</span>
								</span>
							`
						: nothing
				}
			</div>
		`
	}
}
